/**
 * Диалог подтверждения сброса AI-счётчиков (фича 031, US2).
 *
 * Native <dialog>.showModal() — встроенный focus trap + ESC handling.
 * Шаблон из `delete-set-dialog.tsx`.
 */

import { Loader2 } from "lucide-react";
import { useEffect, useId, useRef } from "react";

interface ResetConfirmDialogProps {
	modelName: string;
	promptTokens: number;
	completionTokens: number;
	onCancel: () => void;
	onConfirm: () => void;
	isPending: boolean;
}

export function ResetConfirmDialog({
	modelName,
	promptTokens,
	completionTokens,
	onCancel,
	onConfirm,
	isPending,
}: ResetConfirmDialogProps) {
	const titleId = useId();
	const dialogRef = useRef<HTMLDialogElement>(null);

	useEffect(() => {
		const el = dialogRef.current;
		if (el && !el.open) el.showModal();
	}, []);

	useEffect(() => {
		const el = dialogRef.current;
		if (!el) return;
		const handler = (e: Event) => {
			e.preventDefault();
			onCancel();
		};
		el.addEventListener("cancel", handler);
		return () => el.removeEventListener("cancel", handler);
	}, [onCancel]);

	return (
		<dialog
			ref={dialogRef}
			className="fixed inset-0 m-auto w-[calc(100%-2rem)] max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-lg bg-white p-6 shadow-xl backdrop:bg-black/50"
			aria-labelledby={titleId}
		>
			<h3 id={titleId} className="text-lg font-semibold text-slate-900">
				Сбросить счётчики?
			</h3>
			<p className="mt-2 text-sm text-slate-600">
				Текущие значения для модели <span className="font-mono">{modelName}</span> —{" "}
				<span className="font-mono">{promptTokens.toLocaleString("ru-RU")}</span> входящих /{" "}
				<span className="font-mono">{completionTokens.toLocaleString("ru-RU")}</span> исходящих
				токенов будут архивированы. Накопление продолжится с нуля.
			</p>
			<p className="mt-2 text-xs text-slate-500">
				Данные по другим моделям остаются нетронутыми. История сбросов сохраняется в системе
				аудита.
			</p>
			<div className="mt-6 flex justify-end gap-2">
				<button
					type="button"
					onClick={onCancel}
					disabled={isPending}
					className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
				>
					Отмена
				</button>
				<button
					type="button"
					onClick={onConfirm}
					disabled={isPending}
					className="inline-flex items-center rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50"
				>
					{isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
					Сбросить
				</button>
			</div>
		</dialog>
	);
}
